import { Component, HostListener, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchPropertyPipe } from 'src/app/pipes/search-property.pipe';
import { HttpService } from 'src/app/services/http.service';
import { LanguageService } from 'src/app/services/language.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ErrorDialogData } from '../../modals/error-dialog-data';
import { ErrorDialogComponent } from '../../modals/error-dialog/error-dialog.component';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogData } from '../../modals/confirmation-dialog-data';
import { AddRuleComponent } from '../add-rule/add-rule.component';
import { Rule } from '../rule';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-start-approval-rollout',
  templateUrl: './start-approval-rollout.component.html',
  styleUrl: './start-approval-rollout.component.scss',
  standalone: false
})
export class StartApprovalRolloutComponent {

  /** Previously added bikes */
  public assignedBikes: { id: string, checked: boolean }[] = [];
  public assignedBikesError: boolean = false;
  /** New Bikes to add */
  public newBikes: { id: string, checked: boolean }[] = [];
  public newBikeId: string = "";
  public newIdsSearch: string = "";
  public idsSearch: string = "";
  public releaseId: number = 0;

  public loading: boolean = false;
  private shiftPressed: boolean = false;
  private skipConfirmation: boolean = false;
  public duplicationError: boolean = false;
  public backendError: string = "";
  public fileName: string = "";
  public componentName: string = "";
  public variantName: string = "";

  /** Shift keypress detection  */
  @HostListener('document:keydown', ['$event'])
  handleKeydownEvent(event: KeyboardEvent) {
    if (event.shiftKey) {
      this.shiftPressed = true;
    }
  }

  /** Stop shift keypress detection  */
  @HostListener('document:keyup', ['$event'])
  handleKeyupEvent(event: KeyboardEvent) {
    if (!event.shiftKey) {
      this.shiftPressed = false;
    }
  }

  @HostListener('document:visibilitychange', ['$event'])
  Visibilitychange() {
    this.shiftPressed = false;
  }

  @ViewChild('rejectionRule')
  rejectionRuleRef!: AddRuleComponent;
  private rejectionInitValues: any;

  @ViewChild('approvalRule')
  approvalRuleRef!: AddRuleComponent;
  private approvalInitValues: any;

  constructor(public languageService: LanguageService, private httpService: HttpService,
    private route: ActivatedRoute, private matDialog: MatDialog, private utilsService: UtilsService,
    private snackBar: MatSnackBar, private searchPropertyPipe: SearchPropertyPipe, private router: Router) { }

  /** Init configuration or rollout group name and bike list */
  ngOnInit(): void {
    this.route.queryParams
      .subscribe(params => {
        this.releaseId = params['id'];
        if (params['id'] == undefined) {
          this.router.navigate(["/software-overview"]);
          return;
        }

        this.loading = true;

        forkJoin([this.httpService.getApprovalBikeList(this.releaseId), this.httpService.getRelease(this.releaseId)]).subscribe((resp: any) => {
          if (resp[0].success) {
            this.newBikes = resp[0].data?.map((bikeEl: any) => { return { id: bikeEl.serial_id, checked: false } });
          }
          if (resp[1].success) {
            this.fileName = resp[1].data.filename;
            this.componentName = resp[1].data.component_variant.component.component_name;
            this.variantName = resp[1].data.component_variant.variant_name;
          }

          this.loading = false;
        });
      });
  }

  ngAfterViewInit() {
    this.rejectionInitValues = this.rejectionRuleRef.getRules();
    this.approvalInitValues = this.approvalRuleRef.getRules();
  }

  /** Parse new bike ids string and add to array from input */
  addValuesToNewBikeIdsArray(textValue: string): void {
    if (textValue) {
      const newBikeIdList = textValue.split(/\r?\n/);
      this.processParsedData(newBikeIdList);
    }
  }

  /** Parse new bike ids string and add to array from csv upload */
  addValuesToNewBikeIdsArrayFromCSV(csvTextValue: string): void {
    if (csvTextValue) {
      const newBikeIdList = this.utilsService.parseCsvStringToArray(csvTextValue);
      this.processParsedData(newBikeIdList);
    }
  }

  processParsedData(newBikeIdList: string[]) {
    let bikeIdErrorIds: string[] = [];

    newBikeIdList.forEach((el: string) => {
      if (/^[a-zA-Z0-9]+$/.test(el) == false || el.length > 32) {
        bikeIdErrorIds.push(el);
      }
    });

    if (bikeIdErrorIds.length > 0) {
      const data: ErrorDialogData = {
        title: this.languageService.languageJSON.ConfigurationAssignBikes_InvalidIdsTitle,
        message: this.languageService.languageJSON.ConfigurationAssignBikes_BikeIdError,
        elementList: bikeIdErrorIds
      };
      this.matDialog.open(ErrorDialogComponent, { data: { data } });
    }
    else {
      newBikeIdList.forEach((el: string) => {
        this.addElementToArray(el.toUpperCase(), this.newBikes);
      });
    }
  }

  /** Adding element into array, avoiding redundancy */
  addElementToArray(el: string, array: { id: string, checked: boolean }[]): void {
    if (!array.find((arrEl) => { return arrEl.id == el }) && !this.assignedBikes.find((arrEl) => { return arrEl.id == el }) && el != null && el != undefined && el != "") {
      array.push({ id: el, checked: false });
    } else {
      this.snackBar.open(this.languageService.languageJSON.ConfigurationAssignBikes_Redundancy, "", { duration: 5000 });
    }
  }

  public lastSelectedIndexBikeIds: number = -1;

  /** Multiple bike id selection by pressing shift key */
  shiftSelectBikeId(event: MatCheckboxChange, index: number): void {
    if (this.shiftPressed && this.lastSelectedIndexBikeIds != -1) {
      let assignedBikesRef: { id: string, checked: boolean }[] = this.searchPropertyPipe.transform(this.assignedBikes, 'id', this.idsSearch) as { id: string, checked: boolean }[];
      if (index < this.lastSelectedIndexBikeIds) {
        for (let i = this.lastSelectedIndexBikeIds; i > index; i--) {
          assignedBikesRef[i].checked = event.checked;
        }
      }
      if (index > this.lastSelectedIndexBikeIds) {
        for (let i = this.lastSelectedIndexBikeIds; i < index; i++) {
          assignedBikesRef[i].checked = event.checked;
        }
      }
    }
    this.lastSelectedIndexBikeIds = index;
  }

  public lastSelectedIndexNewBikeIds: number = -1;

  /** Multiple new bike id selection by pressing shift key */
  shiftSelectNewBikeId(event: MatCheckboxChange, index: number): void {
    if (this.shiftPressed && this.lastSelectedIndexNewBikeIds != -1) {
      let newBikesRef: { id: string, checked: boolean }[] = this.searchPropertyPipe.transform(this.newBikes, 'id', this.newIdsSearch) as { id: string, checked: boolean }[];

      if (index < this.lastSelectedIndexNewBikeIds) {
        for (let i = this.lastSelectedIndexNewBikeIds; i > index; i--) {
          newBikesRef[i].checked = event.checked;
        }
      }
      if (index > this.lastSelectedIndexNewBikeIds) {
        for (let i = this.lastSelectedIndexNewBikeIds; i < index; i++) {
          newBikesRef[i].checked = event.checked;
        }
      }
    }
    this.lastSelectedIndexNewBikeIds = index;
  }

  /** Collecting checked bike ids for delete */
  getDeleteBikeIds(): { id: string, checked: boolean }[] {
    return this.assignedBikes.filter((el) => { return el.checked });
  }

  /** Collecting selected new bike ids for delete */
  getDeleteNewBikeIds(): { id: string, checked: boolean }[] {
    return this.newBikes.filter((el) => { return el.checked });
  }

  /** Drag and drop file upload entry point */
  onFileDropped(event: any): void {
    this.readFile(event[0]);
  }

  /** File browse file upload entry point */
  fileBrowseHandler(event: any): void {
    this.readFile(event.target.files[0]);
  }

  /** Parsing file */
  readFile(file: any): void {
    if (file) {
      if (file.type == "application/vnd.ms-excel" || file.type == "text/csv") {
        let reader = new FileReader();

        reader.addEventListener("load", (event: any) => {
          this.addValuesToNewBikeIdsArrayFromCSV(event.target.result.toString());
          this.clearFileInput();
        }, false);

        if (file) {
          reader.readAsText(file);
        }
      }
      else {
        // invalid file
        const data: ErrorDialogData = {
          title: this.languageService.languageJSON.ConfigurationAssignBikes_FileUploadError,
          message: this.languageService.languageJSON.ComponentRecalledUploads_InvalidFileFormat
        };
        this.matDialog.open(ErrorDialogComponent, { data: { data } });
      }
    }
  }

  /** Reset upload file value */
  clearFileInput(): void {
    (document.getElementById("fileDropRef") as HTMLInputElement).value = "";
  }

  /** Concat new bike ids to currently assigned bike-ids */
  assignBikeIds(): void {
    this.newBikes.forEach((el, index) => {
      if (el.checked) {
        this.assignedBikes.push({ ...el, checked: false });
      }
    });

    this.newBikes = this.newBikes.filter((el) => { return el.checked == false });
    this.assignedBikesError = this.assignedBikes.length == 0;
  }

  removeBikeIds(): void {
    this.assignedBikes.forEach((el, index) => {
      if (el.checked) {
        this.newBikes.push({ ...el, checked: false });
      }
    });

    this.assignedBikes = this.assignedBikes.filter((el) => { return el.checked == false });
    this.assignedBikesError = this.assignedBikes.length == 0;
  }

  /** Setting new bike ids checked value to false */
  resetNewBikeSelection(): void {
    this.newBikes.forEach((el) => {
      if (el.checked) {
        el.checked = false;
      }
    });
    this.lastSelectedIndexNewBikeIds = -1;
  }

  /** Setting assigned bike ids checked value to false */
  resetAssignedBikeSelection(): void {
    this.assignedBikes.forEach((el) => {
      if (el.checked) {
        el.checked = false;
      }
    });
    this.lastSelectedIndexBikeIds = -1;
  }

  public onSubmit() {
    this.duplicationError = false;
    this.backendError = "";
    if (this.assignedBikes.length == 0) {
      this.assignedBikesError = true;
    }
    else if (this.rejectionRuleRef.isValid() && this.approvalRuleRef.isValid()) {
      const approvalRules = this.approvalRuleRef.getRules();
      const rejectionRules = this.rejectionRuleRef.getRules();

      if (this.duplicationCheck(approvalRules, rejectionRules)) {
        this.duplicationError = true;
        return;
      }

      const data: ConfirmationDialogData = {
        title: this.languageService.languageJSON.StartApprovalRollout_SaveTitle,
        message: this.languageService.languageJSON.StartApprovalRollout_SaveDescription,
        buttons: [this.languageService.languageJSON.Global_Confirm, this.languageService.languageJSON.Global_Cancel]
      };

      this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
        if (result) {
          this.loading = true;
          this.httpService.addStartApprovalRollout(this.releaseId, this.assignedBikes.map((el) => { return el.id }),
            this.approvalRuleRef.getRules(), this.rejectionRuleRef.getRules()).subscribe((resp: any) => {
              this.loading = false;
              if (resp.success) {
                this.skipConfirmation = true;
                this.snackBar.open(this.languageService.languageJSON.ReleaseOverview_SoftwareApprovalRolloutStart.replace("$filename", this.fileName),
                  undefined, { panelClass: 'success-snackbar', duration: 8000 });
                this.router.navigate(["/software-overview"]);
              }
              else {
                if (resp.failed?.error) {
                  this.backendError = resp.failed?.error;
                }
                this.snackBar.open(this.languageService.languageJSON.Global_ServerError_Snackbar, undefined, { panelClass: 'success-snackbar', duration: 5000 });
              }
            });
        }
      });
    }
  }

  private duplicationCheck(rulesArray1: Rule[], rulesArray2: Rule[]): boolean {
    const rulesArray1Serialized = rulesArray1.map((rule) => { return JSON.stringify(rule) });
    const rulesArray2Serialized = rulesArray2.map((rule) => { return JSON.stringify(rule) });

    return rulesArray1Serialized.some((el) => { return rulesArray2Serialized.includes(el) });
  }

  public isFormChanged(): boolean {
    if (this.assignedBikes.length > 0 ||
      JSON.stringify(this.rejectionRuleRef.getRules()) != JSON.stringify(this.rejectionInitValues) ||
      JSON.stringify(this.approvalRuleRef.getRules()) != JSON.stringify(this.approvalInitValues)) {
      return true;
    }
    return false;
  }
}